import {
  ApolloClient,
  DocumentNode,
  NormalizedCacheObject,
  QueryHookOptions,
  useLazyQuery
} from '@apollo/client';
import { useEffect, useState } from 'react';

import { cloneDeep } from 'lodash';

interface ILazyQueryMutateProps<TData, TVars = undefined> {
  /** Apollo client (e.g., dashboardClient). */
  client: ApolloClient<NormalizedCacheObject>;
  /** Fetch query. */
  query: DocumentNode;
  /** Query options such as `variables`, `skip` */
  options: QueryHookOptions<NoInfer<TData>, NoInfer<TVars>>;
  /** Query result is mutable. */
  shouldMutate?: boolean;
}

const useLazyQueryMutable = <TData, TVars = undefined>(
  props: ILazyQueryMutateProps<TData, TVars>
) => {
  const { client, options, query, shouldMutate } = props;
  const [fetchQuery, { loading, error, data }] = useLazyQuery<TData, TVars>(
    query,
    { ...options, client }
  );

  const [clonedData, setClonedData] = useState<TData | null>(null);

  useEffect(() => {
    if (data && shouldMutate) {
      const cloned = cloneDeep(data);
      setClonedData(cloned);
    } else {
      setClonedData(data);
    }
  }, [data, shouldMutate, client, query]);

  return { loading, error, data: clonedData, fetchQuery };
};

export default useLazyQueryMutable;
